/**
 * Vue Plugin for Interaction Tracker
 */

import type { EffectScope, InjectionKey } from "vue";
import { effectScope, inject, watch } from "vue";
import { Router } from "vue-router";
import {
  InteractionTracker,
  type InteractionResult,
  type InteractionSummary,
  type TimingInfo,
  type TrackedInteraction,
  type TrackedOperation,
  type TrackerConfig,
  type TrackingStatus,
} from "./core";

const TRACKER_KEY: InjectionKey<InteractionTracker | null> = Symbol(
  "interaction-tracker"
);

export interface TrackerPluginOptions extends Partial<TrackerConfig> {
  router?: Router;
  getters?: Record<string, () => any>;
}

export let interactionTracker: InteractionTracker | null = null;

// this is exported for testing purposes
export const createTracker = (
  options?: TrackerConfig
): InteractionTracker | null => {
  try {
    if (!interactionTracker) {
      interactionTracker = new InteractionTracker(options);
    }
    return interactionTracker;
  } catch (error) {
    console.warn("[InteractionTracker] Failed to create tracker:", error);
    return null;
  }
};

export const removeTracker = () => {
  interactionTracker = null;
};

export const interactionTrackerPlugin = {
  install(app, options?: TrackerPluginOptions) {
    try {
      const { router, getters, ...restOptions } = options ?? {};
      createTracker(restOptions as TrackerConfig);

      let scope: EffectScope | null = null;

      try {
        scope = effectScope();
      } catch (error) {
        console.warn(
          "[InteractionTracker] Failed to create effect scope:",
          error
        );
      }

      if (scope && "onUnmount" in app && typeof app.onUnmount === "function") {
        scope.run(() => {
          // Watch router for route changes
          if (router?.currentRoute) {
            try {
              watch(
                () => router.currentRoute.value,
                (route) => {
                  try {
                    interactionTracker?.setGlobalMetadata({
                      routeName: (route?.name as string) ?? undefined,
                      // Default locationId from route params (can be overridden by getters)
                      locationId:
                        (route?.params?.location_id as string) ?? undefined,
                    });
                  } catch (error) {
                    // Silently ignore metadata errors
                  }
                },
                { immediate: true }
              );
            } catch (error) {
              console.warn(
                "[InteractionTracker] Failed to watch router:",
                error
              );
            }
          }

          // Watch any custom getters provided by the consuming app
          if (getters && typeof getters === "object") {
            Object.entries(getters).forEach(([key, getter]) => {
              if (typeof getter === "function") {
                try {
                  watch(
                    getter,
                    (value) => {
                      try {
                        interactionTracker?.setGlobalMetadata({ [key]: value });
                      } catch (error) {
                        // Silently ignore metadata errors
                      }
                    },
                    { immediate: true }
                  );
                } catch (error) {
                  console.warn(
                    `[InteractionTracker] Failed to watch getter "${key}":`,
                    error
                  );
                }
              }
            });
          }
        });
      }

      // Provide tracker (fail-safe)
      try {
        app.provide(TRACKER_KEY, interactionTracker);
      } catch (error) {
        console.warn("[InteractionTracker] Failed to provide tracker:", error);
      }

      try {
        if (app.config?.globalProperties) {
          app.config.globalProperties.$tracker = interactionTracker;
        }
      } catch (error) {
        console.warn(
          "[InteractionTracker] Failed to set global property:",
          error
        );
      }

      if (scope && "onUnmount" in app && typeof app.onUnmount === "function") {
        try {
          app.onUnmount(() => {
            try {
              scope?.stop();
            } catch (error) {
              // Silently ignore cleanup errors
            }
            scope = null;
          });
        } catch (error) {
          console.warn(
            "[InteractionTracker] Failed to register unmount handler:",
            error
          );
        }
      }
    } catch (error) {
      console.warn("[InteractionTracker] Plugin installation failed:", error);
    }
  },
};

/**
 * Get the global tracker instance
 * Returns null if tracker is not installed (fail-safe)
 */
export function useTracker(): InteractionTracker | null {
  try {
    const tracker = inject(TRACKER_KEY);
    return tracker ?? null;
  } catch (error) {
    console.warn("[InteractionTracker] Failed to inject tracker:", error);
    return null;
  }
}

/**
 * Get the global tracker instance (throws if not installed)
 * Use this only when you need to ensure tracker exists
 */
export function useTrackerStrict(): InteractionTracker {
  const tracker = useTracker();
  if (!tracker) {
    throw new Error(
      "InteractionTrackerPlugin not installed. Did you forget app.use(InteractionTrackerPlugin)?"
    );
  }
  return tracker;
}

export type {
  InteractionResult,
  InteractionSummary,
  TimingInfo,
  TrackedInteraction,
  TrackedOperation,
  TrackerConfig,
  TrackingStatus,
};

export { InteractionTracker };
